/**
 * Host-side bridge controller (ADR-001).
 *
 * Runs on the getjar.com PARENT page. Listens for `postMessage` events,
 * validates the sender origin against an allowlist (per `allowed_domains` /
 * `ALLOWED_PARENT_DOMAINS`), validates the envelope structurally via
 * protocol.ts, and routes the three game-to-host message types:
 *
 *   - `handshake`     → reply `handshake_ack { features }` to signal presence.
 *   - `query_ready`   → delegate to `onQueryReady` (stub: ready=false).
 *   - `request_show`  → delegate to `onRequestShow` (stub: no_ad+not_implemented).
 *
 * This file owns ONLY the messaging plumbing. The actual rendering pipeline
 * (GPT/IMA + pacing gate + reward validation) will be wired into the handler
 * callbacks in the follow-on ADR rollout steps. Until then the stubs let the
 * bridge foundation ship and be tested end-to-end.
 *
 * Security model:
 *   - Origin allowlist enforced on EVERY inbound message before parsing.
 *   - Responses are posted back to `event.source` with `event.origin` as the
 *     targetOrigin — never '*' — so a spoofed frame cannot redirect them.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { buildMessage, parseMessage, } from '../bridge/protocol';
const DEFAULT_SHOW_HANDLER = () => __awaiter(void 0, void 0, void 0, function* () {
    return ({
        outcome: 'no_ad',
        reason: 'not_implemented',
    });
});
const DEFAULT_READY_HANDLER = (payload) => __awaiter(void 0, void 0, void 0, function* () {
    return ({
        format: payload.format,
        ready: false,
    });
});
export class HostController {
    constructor(config) {
        var _a, _b, _c, _d;
        this.listenerAttached = false;
        this.allowedOrigins = new Set(config.allowedOrigins);
        this.features = (_a = config.features) !== null && _a !== void 0 ? _a : {};
        this.ownWindow = (_b = config.ownWindow) !== null && _b !== void 0 ? _b : (typeof window !== 'undefined' ? window : null);
        this.onRequestShow = (_c = config.onRequestShow) !== null && _c !== void 0 ? _c : DEFAULT_SHOW_HANDLER;
        this.onQueryReady = (_d = config.onQueryReady) !== null && _d !== void 0 ? _d : DEFAULT_READY_HANDLER;
        this.onHandshake = config.onHandshake;
        this.listener = (event) => {
            // Fire-and-forget — postMessage is one-way, async errors only matter
            // for logging. We swallow rejections so a handler bug can't crash the
            // page.
            void this.handleMessage(event);
        };
    }
    /** Attach the postMessage listener. Idempotent. */
    start() {
        if (this.listenerAttached || !this.ownWindow)
            return;
        this.ownWindow.addEventListener('message', this.listener);
        this.listenerAttached = true;
    }
    /** Detach the listener. Idempotent. */
    stop() {
        if (!this.listenerAttached || !this.ownWindow)
            return;
        this.ownWindow.removeEventListener('message', this.listener);
        this.listenerAttached = false;
    }
    // --- internals -----------------------------------------------------------
    handleMessage(event) {
        return __awaiter(this, void 0, void 0, function* () {
            // 1. Origin gate. Drop anything not from a known game frame BEFORE we
            //    even parse — never spend cycles on attacker-controlled payloads.
            if (!this.allowedOrigins.has(event.origin))
                return;
            // 2. Structural gate. parseMessage enforces namespace + version + known
            //    type + non-empty correlationId.
            const parsed = parseMessage(event.data);
            if (!parsed.ok)
                return;
            const message = parsed.message;
            // 3. Source must be a Window we can post back to. Cross-origin
            //    `event.source` is always restricted but postMessage is allowed.
            const source = event.source;
            if (!source)
                return;
            switch (message.type) {
                case 'handshake':
                    this.replyHandshake(message, event.origin, source);
                    return;
                case 'query_ready':
                    yield this.replyQueryReady(message, event.origin, source);
                    return;
                case 'request_show':
                    yield this.replyRequestShow(message, event.origin, source);
                    return;
                // Messages of host-to-game types arriving from a game frame are
                // unsolicited and ignored (not an error — the spec just doesn't expect
                // them).
                default:
                    return;
            }
        });
    }
    replyHandshake(message, origin, source) {
        const ack = buildMessage('handshake_ack', message.correlationId, {
            features: this.features,
        });
        source.postMessage(ack, origin);
        if (this.onHandshake) {
            try {
                this.onHandshake(message.payload, origin);
            }
            catch (err) {
                // Observer errors must not affect downstream routing.
                // eslint-disable-next-line no-console
                console.warn('[GetJarAds host] onHandshake observer threw:', err);
            }
        }
    }
    replyQueryReady(message, origin, source) {
        return __awaiter(this, void 0, void 0, function* () {
            let result;
            try {
                result = yield this.onQueryReady(message.payload, {
                    origin,
                    source,
                });
            }
            catch (err) {
                result = { format: message.payload.format, ready: false };
                // eslint-disable-next-line no-console
                console.warn('[GetJarAds host] onQueryReady threw, replying ready:false:', err);
            }
            source.postMessage(buildMessage('ready_state', message.correlationId, result), origin);
        });
    }
    replyRequestShow(message, origin, source) {
        return __awaiter(this, void 0, void 0, function* () {
            let result;
            try {
                result = yield this.onRequestShow(message.payload, {
                    origin,
                    source,
                });
            }
            catch (err) {
                result = { outcome: 'error', reason: errorReason(err) };
            }
            source.postMessage(buildMessage('ad_result', message.correlationId, result), origin);
        });
    }
}
function errorReason(err) {
    if (err instanceof Error)
        return err.message || err.name || 'unknown';
    if (typeof err === 'string')
        return err;
    return 'unknown';
}
